import React, { useMemo } from 'react'
import { IntlShape, useIntl } from 'react-intl'
import { Column } from '@web/walter-ui-kit/components/Table/types'
import { AppSvg, FormattedHTMLMessage, newIcons, useBreakpoints } from '@web/walter-ui-kit'
import { GetCard } from '@web/walter-lib/api'
import { GetThreeDSChallengesReturn } from '@web/walter-lib/api/types'
import { lang } from './lang'
import s from './s.module.css'
import CardImage from '~pages/CardsPage/components/CardImage'
import { useGetThreeDSChallengesData } from '~api'
import StatusBadge from '~pages/CardsPage/components/StatusBadge'

const cardColumn = (intl: IntlShape, data?: GetThreeDSChallengesReturn): Column<GetCard> => ({
    key: 'card',
    title: intl.formatMessage(lang.card),
    sortable: false,
    headerClassName: s.cardHeader,
    render: d => (
        <div className={s.cardColumn}>
            <CardImage
                designId={d?.card_design?.id}
                className={s.img}
                status={d?.status}
                showBlurredText={false}
                blurredImgSize={24}
            />
            <span>·· {d?.masked_number || '****'}</span>
            {data?.challenges?.some(ch => ch?.card_id === d?.id) && (
                <AppSvg svgName={newIcons.ic24Info} size={24} path="new-icons" className={s.alert} />
            )}
        </div>
    ),
})

const getTypeWidth = (bp: {
    isMobile: boolean
    isTablet: boolean
    isTabletOverview: boolean
    isTabletLandscape: boolean
    isDesktop: boolean
}) => {
    if (bp.isMobile || bp.isTablet || bp.isTabletOverview) {
        return 84
    }

    if (bp.isTabletLandscape) {
        return 180
    }

    return 230
}

const typeColumn = (
    intl: IntlShape,
    bp: {
        isMobile: boolean
        isTablet: boolean
        isTabletOverview: boolean
        isTabletLandscape: boolean
        isDesktop: boolean
    }
): Column<GetCard> => ({
    key: 'type',
    title: intl.formatMessage(lang.type),
    width: getTypeWidth(bp),
    sortable: false,
    headerClassName: s.typeHeader,
    render: d => {
        if (d.card_type === 'VIRTUAL') {
            return <FormattedHTMLMessage {...lang.virtual} className={s.tableText} />
        }

        if (d.card_type === 'PHYSICAL') {
            return <FormattedHTMLMessage {...lang.physical} className={s.tableText} />
        }

        return d.card_type
    },
})

const statusColumn = (intl: IntlShape): Column<GetCard> => ({
    key: 'status',
    title: intl.formatMessage(lang.status),
    width: 104,
    sortable: false,
    headerClassName: s.statusHeader,
    render: d => {
        return <StatusBadge card={d} />
    },
})

const getColumns = (
    intl: IntlShape,
    bp: {
        isMobile: boolean
        isTablet: boolean
        isTabletOverview: boolean
        isTabletLandscape: boolean
        isDesktop: boolean
    },
    data?: GetThreeDSChallengesReturn
) => {
    return [cardColumn(intl, data), typeColumn(intl, bp), statusColumn(intl)]
}

export const useColumns = () => {
    const intl = useIntl()
    const bp = useBreakpoints()
    const { data } = useGetThreeDSChallengesData()

    const columns = useMemo(() => getColumns(intl, bp, data), [intl, bp, data])

    return columns
}
